import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRegisterMutation, useUpdateMutation } from '../../app/services/supplies';
import { fetchSupplie, fetchSupplies } from '../../features/supplies/suppliesSlice';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import { iconButtonStyles } from '../../utils/iconButtonStyles';
import { MODAL_STYLES } from '../../utils/modalStyles';
import DragDropFileUpload from '../DragDropFileUpload';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import {
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { ModalDialogActions, ModalButton, ModalCloseButton, ModalAddButton } from '../ModalComponents';
import EditIcon from '@mui/icons-material/Edit';

import styles from '../ModalComponent/modalStyles.module.css';
import { useTranslation } from 'react-i18next';
import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { postSupplySchema, putSupplySchema } from '../../schemas';
import { INPUT_FILE_ACCEPT_DOC } from '../../constants';

const initialForm = {
  number: '',
  name: '',
  description: '',
  manufacture: '',
};

export default function ModalAddSupplie({ id, type }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [registerSupplies] = useRegisterMutation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const supplyNumError = getValidationErrorMessage(formValidationErrors, 'number');
  const supplyNameError = getValidationErrorMessage(formValidationErrors, 'name');
  const supplyFileError = getValidationErrorMessage(formValidationErrors, 'file');

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [updateSupplie] = useUpdateMutation();

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => {
    setOpen(true);
  };

  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setSelectedFile(null);
    setErrorResponse('');
    setFormValidationErrors(null);
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (id && open) {
      async function fetchData() {
        try {
          const response = await dispatch(fetchSupplie(id));
          setForm({
            number: response.payload.number,
            name: response.payload.name,
            description: response.payload.description,
            manufacture: response.payload.manufacture,
          });
        } catch (error) {
          console.error('Error fetching supplie:', error);
        }
      }
      fetchData();
    }
  }, [id, open, dispatch]);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    if (id) {
      const validationErrors = validateFormData(t, putSupplySchema, form);
      if (!!validationErrors) {
        setFormValidationErrors(validationErrors);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('number', form.number);
        formData.append('name', form.name);
        formData.append('description', form.description);
        formData.append('manufacture', form.manufacture);
        if (selectedFile) {
          formData.append('file', selectedFile);
        }
        const resp = await updateSupplie({ id, formData });
        if (resp.error) {
          setErrorResponse(resp.error.data?.message);
        } else {
          doClose();
          dispatch(fetchSupplies());
        }
      } catch (err) {
        console.error('Error registering supplie:', err);
      }
    } else {
      const validationErrors = validateFormData(t, postSupplySchema, {
        ...form,
        file: selectedFile,
      });
      if (!!validationErrors) {
        setFormValidationErrors(validationErrors);
        return;
      }
      try {
        const formData = new FormData();
        formData.append('number', form.number);
        formData.append('name', form.name);
        formData.append('description', form.description);
        formData.append('manufacture', form.manufacture);
        formData.append('file', selectedFile);
        const resp = await registerSupplies(formData);
        if (resp.error) {
          setErrorResponse(resp.error.data?.message);
        } else {
          doClose();
          dispatch(fetchSupplies());
        }
      } catch (err) {
        console.error('Error registering supplie:', err);
      }
    }
  };

  return (
    <div>
      {type === 'add' ? (
        <ModalAddButton onClick={handleOpen} />
      ) : (
        <Tooltip title={t('generic.edit')} placement="top">
          <IconButton color="primary" onClick={handleOpen} style={iconButtonStyles}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={MODAL_STYLES.dialogTitle}>
            {type === 'add' ? t('modals.createSupplie') : t('modals.updateSupplie')}
          </DialogTitle>
          <ModalCloseButton onClick={handleClose} />
          <DialogContent dividers sx={MODAL_STYLES.dialogContentWithScroll}>
            <div style={MODAL_STYLES.formContainer}>
              <TextField
                label={t('generic.number')}
                variant="outlined"
                value={form.number}
                name="number"
                onChange={handleChange}
                fullWidth
                error={!!supplyNumError}
                helperText={supplyNumError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.name')}
                variant="outlined"
                value={form.name}
                name="name"
                onChange={handleChange}
                fullWidth
                error={!!supplyNameError}
                helperText={supplyNameError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.description')}
                variant="outlined"
                value={form.description}
                name="description"
                onChange={handleChange}
                fullWidth
                helperText=" "
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.manufacture')}
                variant="outlined"
                value={form.manufacture}
                name="manufacture"
                onChange={handleChange}
                fullWidth
                helperText=" "
                sx={commonTextFieldStyles}
              />
              <DragDropFileUpload
                accept={INPUT_FILE_ACCEPT_DOC}
                selectedFile={selectedFile}
                onFileChange={(file, error) => {
                  setIsDirty(true);
                  if (error) {
                    setErrorResponse(error);
                    setSelectedFile(null);
                  } else {
                    setErrorResponse('');
                    setSelectedFile(file);
                  }
                }}
                onFileRemove={() => {
                  setIsDirty(true);
                  setSelectedFile(null);
                  setErrorResponse('');
                }}
                error={supplyFileError}
                helperText={t('generic.uploadFileSupplyRecord')}
                maxSize={process.env.REACT_APP_MAX_FILE_SIZE || 10485760}
                placeholder={t('modals.uploadDocument')}
              />

              {errorResponse &&
                (Array.isArray(errorResponse) ? (
                  errorResponse.map((item, index) => (
                    <Typography key={index} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                ))}
            </div>
          </DialogContent>
          <ModalDialogActions>
            <ModalButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              {type === 'add' ? t('modals.create') : t('modals.update')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
